import React, { FC } from 'react';
import { useMutation } from '@apollo/client';
import { useRouter } from 'next/compat/router';
import ConfirmationModalContent from '../ConfirmationModal/ConfirmationModalContent';
import { TGetModalComponent } from '../Modal/useModal';
import { deletePromptMutation } from './mutations/deletePromptMutation';
import { showNotification } from '../NotificationBanner/showNotification';
import ConfirmationModalActions from '../ConfirmationModal/ConfirmationModalActions';
import getTerritoryPageUrl from '../../services/pagesUrl/getTerritoryPageUrl';
import { getSortedPromptsQuery } from '../Prompt/queries/getSortedPromptsQuery';

import { TERRITORY_PROMPTS_SIZE } from '../Territory/TerritoryPromptsSection/constants';
import { ArticleType } from '../Article/types';
import { deleteLivePromptMutation } from './mutations/deleteLivePromptMutation';

type TDeletePromptConfirmationModalProps = {
  ModalComponent: TGetModalComponent;
  closeModal: () => void;
  isLoading?: boolean;
  promptId: string;
  territorySlug: string;
  territoryId: string;
  promptSettingsId?: string;
};

export const DeletePromptConfirmationModal: FC<TDeletePromptConfirmationModalProps> = ({
  ModalComponent,
  isLoading,
  promptId,
  territorySlug,
  closeModal,
  territoryId,
  promptSettingsId,
}) => {
  const router = useRouter();
  const mutation = promptSettingsId ? deleteLivePromptMutation : deletePromptMutation;
  const [deletePrompt] = useMutation(mutation, {
    variables: {
      promptId,
      promptSettingsId,
    },
    refetchQueries: [
      {
        query: getSortedPromptsQuery,
        variables: {
          skip: 0,
          take: TERRITORY_PROMPTS_SIZE,
          where: { type: { equals: ArticleType.Prompt }, territory: { id: { equals: territoryId } } },
        },
      },
    ],
  });

  const handleConfirm = async () => {
    try {
      await deletePrompt();
    } catch (error) {
      showNotification({
        type: 'error',
        message: 'Something went wrong. Please try again.',
      });

      return;
    }

    closeModal();
    showNotification({
      type: 'success',
      message: 'Your prompt has been successfully deleted.',
    });
    router?.push(getTerritoryPageUrl(territorySlug));
  };

  return (
    <ModalComponent
      containerClassName="!top-[25%]"
      title="Delete prompt"
      footer={
        <ConfirmationModalActions
          handleCancel={closeModal}
          handleConfirm={handleConfirm}
          isLoading={isLoading}
          confirmationButtonText="Delete"
        />
      }
    >
      <ConfirmationModalContent body="Deleting the prompt will remove it completely. Replies to this prompt will not be deleted. This action cannot be undone." />
    </ModalComponent>
  );
};
